import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import React, { ChangeEvent, FormEvent, useState, useEffect } from "react";
import Swal from "sweetalert2";
import { showToast } from "../Toast";
import { ForgetPasswordData } from "../../types/auth.types";
import { __fnGetFormatedCurrentDate } from "../../utilities/Common";
import PKSOFT_URLS from "../../utilities/Pk_api_Urls";

interface ForgotPasswordFormProps {
  onSwitchToLogin: () => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onSwitchToLogin }) => {
  const [form, setForm] = useState<ForgetPasswordData>({
    username: "",
    newPassword: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  /* ----------------------- OTP STATE ----------------------- */
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otpToken, setOtpToken] = useState<string>("");
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [isVerifying, setIsVerifying] = useState(false);

  /* ----------------------- CAPTCHA STATE ----------------------- */
  const [captchaQuestion, setCaptchaQuestion] = useState<string>("");
  const [captchaAnswer, setCaptchaAnswer] = useState<number>(0);
  const [userCaptchaInput, setUserCaptchaInput] = useState<string>("");

  useEffect(() => {
    generateCaptcha();
  }, []);

  useEffect(() => {
    if (!isOtpSent) {
      generateCaptcha();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOtpSent]);

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    const operators = ["+", "-", "*"];
    const operator = operators[Math.floor(Math.random() * operators.length)];

    let answer = 0;
    switch (operator) {
      case "+":
        answer = num1 + num2;
        break;
      case "-":
        answer = num1 - num2;
        break;
      case "*":
        answer = num1 * num2;
        break;
    }

    setCaptchaQuestion(`${num1} ${operator} ${num2} = ?`);
    setCaptchaAnswer(answer);
    setUserCaptchaInput("");
  };

  /* ----------------------- OTP HANDLERS ----------------------- */
  useEffect(() => {
    if (isOtpSent) {
      const firstInput = document.getElementById("forgot-otp-0");
      firstInput?.focus();
    }
  }, [isOtpSent]);

  const handleOtpChange = (value: string, index: number) => {
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 5) {
        const nextInput = document.getElementById(`forgot-otp-${index + 1}`);
        nextInput?.focus();
      }

      if (newOtp.every((digit) => digit !== "")) {
        const verifyBtn = document.getElementById("forgot-verify-otp-btn");
        verifyBtn?.focus();
      }
    }
  };

  const handleOtpKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
        const prevInput = document.getElementById(`forgot-otp-${index - 1}`);
        prevInput?.focus();
      }
    }
  };

  /* ----------------------- VALIDATION ----------------------- */
  const validate = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    if (!form.username.trim()) newErrors.username = "Email is required.";
    else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.username))
      newErrors.username = "Invalid email address.";

    if (!form.newPassword.trim()) {
      newErrors.newPassword = "Password is required.";
    } else if (form.newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters.";
    } else if (!/(?=.*[a-z])/.test(form.newPassword)) {
      newErrors.newPassword = "Password must contain at least one lowercase letter.";
    } else if (!/(?=.*[A-Z])/.test(form.newPassword)) {
      newErrors.newPassword = "Password must contain at least one uppercase letter.";
    } else if (!/(?=.*\d)/.test(form.newPassword)) {
      newErrors.newPassword = "Password must contain at least one number.";
    } else if (!/(?=.*[@$!%*?&])/.test(form.newPassword)) {
      newErrors.newPassword = "Password must contain at least one special character (@$!%*?&).";
    }

    if (!userCaptchaInput.trim()) {
      newErrors.captcha = "Please solve the CAPTCHA.";
    } else if (parseInt(userCaptchaInput) !== captchaAnswer) {
      newErrors.captcha = "Incorrect CAPTCHA answer.";
    }

    return newErrors;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleCaptchaChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUserCaptchaInput(e.target.value);
    if (errors.captcha) {
      setErrors({ ...errors, captcha: "" });
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      if (validationErrors.captcha && userCaptchaInput.trim()) {
        generateCaptcha();
      }
      return;
    }
    setErrors({});
    setIsSaving(true);

    try {
      const response = await axios.post(PKSOFT_URLS.CUSTOMER.FORGET_PASSWORD, {
        ...form,
      });

      const { status, messageCode, message } = response.data;

      if (status === 200) {
        if (messageCode === 100) {
          showToast({ type: "success", message, timer: 3000 });
          setOtpToken(response.data.data?.token || "");
          setIsOtpSent(true);
        } else if (messageCode === 126) {
          showToast({ type: "success", message, timer: 3000 });
          generateCaptcha();
        } else if (messageCode === 108) {
          Swal.fire({
            title: "Password Sent!",
            text: `The password has been sent to your registered email: ${form.username}`,
            icon: "info",
            showCancelButton: true,
            confirmButtonText: "Go to Login",
            cancelButtonText: "Cancel",
          }).then((result) => {
            if (result.isConfirmed) onSwitchToLogin();
          });
        }
      } else {
        showToast({ type: "error", message, timer: 3000 });
        generateCaptcha();
      }
    } catch (err: any) {
      showToast({
        type: "error",
        message: err.response?.data?.message || "Failed to request.",
        timer: 3000,
      });
      generateCaptcha();
    } finally {
      setIsSaving(false);
    }
  };

  const handleVerifyOtp = async () => {
    const otpCode = otp.join("");
    if (otpCode.length < 6) {
      showToast({ type: "error", message: "Please enter all 6 digits", timer: 3000 });
      return;
    }
    setIsVerifying(true);
    try {
      const response = await axios.post(PKSOFT_URLS.CUSTOMER.FORGET_PASSWORD_OTP, {
        username: form.username,
        otpCode,
        newPassword: form.newPassword,
        token: otpToken,
      });

      if (response.data && response.data.messageCode === 100) {
        showToast({ type: "success", message: "Password reset successfully!", timer: 1500 });
        onSwitchToLogin();
      } else if (response.data && response.data.messageCode === 102) {
        showToast({ type: "warning", message: response.data.message, timer: 3000 });
        setOtp(Array(6).fill(""));
        document.getElementById("forgot-otp-0")?.focus();
      } else {
        showToast({ type: "error", message: response.data.message, timer: 3000 });
        setOtp(Array(6).fill(""));
        document.getElementById("forgot-otp-0")?.focus();
      }
    } catch (error: any) {
      showToast({
        type: "error",
        message: error.response?.data?.message || "OTP verification failed.",
        timer: 3000,
      });
      setOtp(Array(6).fill(""));
      document.getElementById("forgot-otp-0")?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="auth-form-wrapper">
      <AnimatePresence mode="wait">
        {!isOtpSent ? (
          /* --- PASSWORD RESET FORM --- */
          <motion.div
            key="reset-form"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-4">
              <h2 className="auth-title">Forget Password</h2>
              <p className="auth-subtitle">Enter your details to recover your account</p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Email */}
              <div className="modern-input-group">
                <label className="modern-label">
                  Email Address <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="username"
                  className={`modern-input ${errors.username ? "is-invalid" : ""}`}
                  placeholder="Enter your email"
                  value={form.username}
                  onChange={handleChange}
                  disabled={isSaving}
                />
                {errors.username && (
                  <div className="invalid-feedback d-block mt-1">{errors.username}</div>
                )}
              </div>

              {/* New Password */}
              <div className="modern-input-group">
                <label className="modern-label">
                  New Password <span className="text-danger">*</span>
                </label>
                <div className="input-with-icon">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="newPassword"
                    className={`modern-input ${errors.newPassword ? "is-invalid" : ""}`}
                    placeholder="Enter your new password"
                    value={form.newPassword}
                    onChange={handleChange}
                    disabled={isSaving}
                  />
                  <button
                    type="button"
                    className="input-icon-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <i className="bi bi-eye-slash-fill fs-5"></i>
                    ) : (
                      <i className="bi bi-eye-fill fs-5"></i>
                    )}
                  </button>
                </div>
                {errors.newPassword && (
                  <div className="invalid-feedback d-block mt-1">{errors.newPassword}</div>
                )}
                <small className="text-muted d-block mt-1">
                  Password must be at least 8 characters with uppercase, lowercase, number, and special character (@$!%*?&)
                </small>
              </div>

              {/* CAPTCHA */}
              <div className="modern-input-group">
                <label className="modern-label">
                  Security Check <span className="text-danger">*</span>
                </label>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <div className="captcha-box" style={{ userSelect: "none" }}>
                    {captchaQuestion}
                  </div>
                  <button
                    type="button"
                    className="captcha-refresh-btn"
                    onClick={generateCaptcha}
                    disabled={isSaving}
                    title="Refresh CAPTCHA"
                  >
                    <i className="bi bi-arrow-clockwise fs-5"></i>
                  </button>
                </div>
                <input
                  type="text"
                  className={`modern-input ${errors.captcha ? "is-invalid" : ""}`}
                  placeholder="Enter your answer"
                  value={userCaptchaInput}
                  onChange={handleCaptchaChange}
                  disabled={isSaving}
                />
                {errors.captcha && (
                  <div className="invalid-feedback d-block mt-1">{errors.captcha}</div>
                )}
              </div>

              {/* Submit */}
              <button type="submit" className="modern-btn mt-4" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Submitting...
                  </>
                ) : (
                  "Recover Password"
                )}
              </button>
            </form>

            {/* Links */}
            <div className="auth-footer justify-content-center mt-3">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  onSwitchToLogin();
                }}
                className="auth-link"
              >
                ← Back to Login
              </a>
            </div>
          </motion.div>
        ) : (
          /* --- OTP VERIFICATION FORM --- */
          <motion.div
            key="otp-form"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-2">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setIsOtpSent(false);
                }}
                className="auth-link auth-link-small"
              >
                ← Back to Form
              </a>
            </div>
            <div className="mb-4 text-center mt-3">
              <h2 className="auth-title">Verify OTP</h2>
              <p className="auth-subtitle">
                We've sent a 6-digit code to <br />
                <strong>{form.username}</strong>
              </p>
            </div>

            <div className="d-flex flex-column align-items-center">
              <div className="otp-container">
                {otp.map((digit, idx) => (
                  <input
                    key={idx}
                    id={`forgot-otp-${idx}`}
                    type="text"
                    maxLength={1}
                    className="modern-input otp-input"
                    value={digit}
                    onChange={(e) => handleOtpChange(e.target.value, idx)}
                    onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                    disabled={isVerifying}
                    title={`OTP digit ${idx + 1}`}
                    placeholder="0"
                  />
                ))}
              </div>

              <button
                id="forgot-verify-otp-btn"
                className="modern-btn mt-4 w-75"
                onClick={handleVerifyOtp}
                disabled={isVerifying}
              >
                {isVerifying ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Verifying...
                  </>
                ) : (
                  "Verify Code"
                )}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ForgotPasswordForm;
